/**
 * Versão do esquema × versão do app — regras PURAS (sem React, sem Supabase).
 *
 * Doc: docs/funcionalidades/versao-do-app-e-do-esquema.md
 *
 * O app traz do BUILD a última migration que espera encontrar no banco
 * (`VITE_ESQUEMA_ESPERADO`, carimbo AAAAMMDDHHMMSS do arquivo mais novo de
 * supabase/migrations). Na inicialização ele pergunta ao banco
 * (`rpc('app_schema_version')`) e decide se pode seguir ou se mostra
 * "Sistema em atualização".
 *
 * A publicação (Komodo) aplica migrations → funções → apps, então em operação
 * normal o banco chega ANTES do app e o banco à frente é o estado comum (e
 * compatível: migration é aditiva até o app novo chegar). O perigo é o
 * contrário — app novo com banco velho, ou migration que falhou no meio — e é
 * só esse que bloqueia.
 */

/** Carimbo de migration: 14 dígitos (scripts/check-migration-trail.mjs). */
const CARIMBO = /^\d{14}$/;

/** Devolve o carimbo limpo ou `null` quando o valor não é um carimbo. */
export function normalizarVersaoDoEsquema(valor: unknown): string | null {
  if (typeof valor !== "string") return null;
  const v = valor.trim();
  return CARIMBO.test(v) ? v : null;
}

/**
 * Compara dois carimbos: negativo se `a` < `b`, 0 se iguais, positivo se `a` > `b`.
 * Mesmo comprimento fixo → a ordem lexicográfica é a numérica.
 */
export function compararVersoesDoEsquema(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** O que a chamada de saúde devolveu, no formato do supabase-js (`{ data, error, status }`). */
export interface RespostaDeSaude {
  data: unknown;
  error: { code?: string | null; message?: string | null } | null;
  /** Status HTTP. O supabase-js devolve 0 quando o fetch nem completou (rede). */
  status?: number | null;
}

/**
 * Por que a guarda saiu do normal (vira tag `motivo` no Sentry):
 * - `banco_atras`     — o banco respondeu uma versão menor que a do build;   } DEFINITIVOS:
 * - `funcao_ausente`  — banco anterior à própria função (PGRST202/42883);    } trocam a tela já
 * - `cache_do_esquema`— PGRST002: o PostgREST está recarregando o esquema;   } TRANSITÓRIOS:
 * - `api_indisponivel`— 503 do PostgREST;                                    } só contam na 3ª
 * - `rede`            — fetch não completou / 502 / 504, navegador online.   } falha seguida
 */
export type MotivoDefinitivo = "banco_atras" | "funcao_ausente";
export type MotivoTransitorio = "cache_do_esquema" | "api_indisponivel" | "rede";
export type MotivoDeAtualizacao = MotivoDefinitivo | MotivoTransitorio;

export type ClassificacaoDeSaude =
  | { tipo: "ok"; versaoDoBanco: string | null }
  | { tipo: "definitivo"; motivo: MotivoDefinitivo; versaoDoBanco: string | null }
  | { tipo: "transitorio"; motivo: MotivoTransitorio }
  /** Erro que NÃO é sinal de banco em atualização (401, 500 qualquer…): segue o app. */
  | { tipo: "ignorar"; codigo: string | null; status: number | null };

const FUNCAO_AUSENTE = new Set(["PGRST202", "42883"]);

/**
 * Classifica uma resposta da chamada de saúde contra a versão esperada.
 *
 * Banco à frente ou igual = ok. Versão do banco ilegível (null, formato
 * estranho) = atrás: um banco com ledger vazio ou sem a função não é o banco
 * que este app espera.
 */
export function classificarRespostaDeSaude(
  esperada: string,
  resposta: RespostaDeSaude,
): ClassificacaoDeSaude {
  const { error } = resposta;
  const status = resposta.status ?? null;

  if (error) {
    const codigo = error.code ? String(error.code) : null;
    if (codigo && FUNCAO_AUSENTE.has(codigo)) {
      return { tipo: "definitivo", motivo: "funcao_ausente", versaoDoBanco: null };
    }
    if (codigo === "PGRST002") return { tipo: "transitorio", motivo: "cache_do_esquema" };
    if (status === 503) return { tipo: "transitorio", motivo: "api_indisponivel" };
    // 0 = fetch não completou; 502/504 = o proxy não alcançou a API.
    if (status === 0 || status === 502 || status === 504) return { tipo: "transitorio", motivo: "rede" };
    return { tipo: "ignorar", codigo, status };
  }

  if (status === 503) return { tipo: "transitorio", motivo: "api_indisponivel" };

  const versaoDoBanco = normalizarVersaoDoEsquema(resposta.data);
  if (versaoDoBanco === null) {
    return { tipo: "definitivo", motivo: "banco_atras", versaoDoBanco: null };
  }
  if (compararVersoesDoEsquema(versaoDoBanco, esperada) < 0) {
    return { tipo: "definitivo", motivo: "banco_atras", versaoDoBanco };
  }
  return { tipo: "ok", versaoDoBanco };
}

/** Falhas TRANSITÓRIAS seguidas (503, PGRST002, rede) até a guarda reagir. */
export const FALHAS_TRANSITORIAS_PARA_REAGIR = 3;

export type DecisaoDeSaude =
  | { estado: "ok"; falhasTransitorias: number }
  /** Troca o app pela página "Sistema em atualização" (desmonta o app). */
  | { estado: "pagina"; motivo: MotivoDeAtualizacao; versaoDoBanco: string | null; falhasTransitorias: number }
  /** Faixa "Conexão instável" POR CIMA do app, sem desmontar nada. */
  | { estado: "faixa"; motivo: MotivoTransitorio; falhasTransitorias: number }
  /** Falha transitória ainda não persistente: nada muda na tela, tenta de novo. */
  | { estado: "repetir"; falhasTransitorias: number };

export interface ContextoDaDecisao {
  /**
   * O app já teve uma resposta OK nesta sessão (está montado e em uso). Aí só
   * os motivos DEFINITIVOS trocam a tela; os transitórios viram, no máximo, a
   * faixa — trocar o children desmontaria o QueryClientProvider e o usuário
   * perderia o formulário por um 503 de segundos.
   */
  montado: boolean;
  /** `navigator.onLine`. Offline a falha de rede é do aparelho: não conta. */
  online: boolean;
}

export function decidirEstadoDeSaude(
  classificacao: ClassificacaoDeSaude,
  falhasAnteriores: number,
  { montado, online }: ContextoDaDecisao,
): DecisaoDeSaude {
  switch (classificacao.tipo) {
    case "ok":
    case "ignorar":
      return { estado: "ok", falhasTransitorias: 0 };
    case "definitivo":
      return {
        estado: "pagina",
        motivo: classificacao.motivo,
        versaoDoBanco: classificacao.versaoDoBanco,
        falhasTransitorias: 0,
      };
    case "transitorio": {
      if (classificacao.motivo === "rede" && !online) {
        return { estado: "repetir", falhasTransitorias: falhasAnteriores };
      }
      const falhas = falhasAnteriores + 1;
      if (falhas < FALHAS_TRANSITORIAS_PARA_REAGIR) return { estado: "repetir", falhasTransitorias: falhas };
      return montado
        ? { estado: "faixa", motivo: classificacao.motivo, falhasTransitorias: falhas }
        : { estado: "pagina", motivo: classificacao.motivo, versaoDoBanco: null, falhasTransitorias: falhas };
    }
  }
}


/**
 * Rótulo da versão do build para o rodapé: `v2026.1011.71 · abc1234`.
 * Sem versão (dev, build fora da imagem) → "dev".
 */
export function rotuloDaVersaoDoApp(versao?: string | null, commit?: string | null): string {
  const v = (versao ?? "").trim();
  const base = v && v !== "dev" ? `v${v.replace(/^v/, "")}` : "dev";
  const sha = (commit ?? "").trim().slice(0, 7);
  return /^[0-9a-f]{7}$/i.test(sha) ? `${base} · ${sha}` : base;
}
